import { DocumentsService } from "@root/services/documentActions";
import { createAsyncThunk } from "@reduxjs/toolkit";

import {
  CREATE_DOCUMENT_ACTION,
  GET_ALL_DOCUMENTS_ACTION,
  GET_DOCUMENT_BY_ID_ACTION,
  PROCESS_DOCUMENT_ACTION,
  CREATE_EXTRACTION_REVIEWS_ACTION,
  GET_EXTRACTION_REVIEWS_ACTION,
  CALL_EXTRACTION_REVIEWS_AI_ACTION,
  GET_EXTRACTION_REVIEW_BY_ID_ACTION,
  DELETE_EXTRACTION_REVIEW_ACTION,
  RETRY_DOCUMENT_ACTION,
} from "@root/store/action-types/documentsActions";
import { getBaseUrl } from "@root/store/selectors/features";
import { isFuncType } from "@root/utils/helpers";

const documentsService = new DocumentsService();

/**
 * Upload document in chunks with progress tracking
 * Now uses /api/documents/upload-chunk endpoint
 */
export const createDocument: any = createAsyncThunk(
  CREATE_DOCUMENT_ACTION,
  async (requestPayload: any, thunkAPI: any) => {
    const { onSuccess, onError, onProgress, formData } = requestPayload;
    try {
      const { data } = await documentsService.createDocument(
        formData,
        onProgress
      );
      console.log("createDocument response data:", data);
      if (isFuncType(onSuccess)) {
        onSuccess(data);
      }
      return thunkAPI.fulfillWithValue(data);
    } catch (error) {
      if (isFuncType(onError)) {
        onError(error);
      }
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const createDocumentWithProgress: any = createAsyncThunk(
  CREATE_DOCUMENT_ACTION,
  async (requestPayload: any, thunkAPI: any) => {
    const { onSuccess, onError, onProgress, formData, signal } = requestPayload;
    try {
      const { data } = await documentsService.createDocumentWithProgress(
        formData,
        onProgress,
        signal
      );
      if (isFuncType(onSuccess)) {
        onSuccess(data);
      }
      return thunkAPI.fulfillWithValue(data);
    } catch (error) {
      if (isFuncType(onError)) {
        onError(error);
      }
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getAllDocuments: any = createAsyncThunk(
  GET_ALL_DOCUMENTS_ACTION,
  async (requestPayload: any, thunkAPI: any) => {
    const { onSuccess, onError, payload } = requestPayload;
    try {
      const { data } = await documentsService.getAllDocuments(payload);
      if (isFuncType(onSuccess)) {
        onSuccess(data);
      }
      return thunkAPI.fulfillWithValue(data);
    } catch (error) {
      if (isFuncType(onError)) {
        onError(error);
      }
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getDocumentById: any = createAsyncThunk(
  GET_DOCUMENT_BY_ID_ACTION,
  async (requestPayload: any, thunkAPI: any) => {
    const { onSuccess, onError, id } = requestPayload;
    try {
      const { data } = await documentsService.getDocumentById(id);
      if (isFuncType(onSuccess)) {
        onSuccess(data);
      }
      return thunkAPI.fulfillWithValue(data);
    } catch (error) {
      if (isFuncType(onError)) {
        onError(error);
      }
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const processDocument: any = createAsyncThunk(
  PROCESS_DOCUMENT_ACTION,
  async (requestPayload: any, thunkAPI: any) => {
    const { onSuccess, onError, id } = requestPayload;
    try {
      const { data } = await documentsService.processDocument({ id });
      if (isFuncType(onSuccess)) {
        onSuccess(data);
      }
      return thunkAPI.fulfillWithValue(data);
    } catch (error) {
      if (isFuncType(onError)) {
        onError(error);
      }
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const processDocumentReview: any = createAsyncThunk(
  PROCESS_DOCUMENT_ACTION,
  async (requestPayload: any, thunkAPI: any) => {
    const { onSuccess, onError, id } = requestPayload;
    try {
      const { data } = await documentsService.processDocumentReview({ id });
      if (isFuncType(onSuccess)) {
        onSuccess(data);
      }
      return thunkAPI.fulfillWithValue(data);
    } catch (error) {
      if (isFuncType(onError)) {
        onError(error);
      }
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const processDocumentLegal: any = createAsyncThunk(
  PROCESS_DOCUMENT_ACTION,
  async (requestPayload: any, thunkAPI: any) => {
    const { onSuccess, onError, id } = requestPayload;
    try {
      const { data } = await documentsService.processDocumentLegal({ id });
      if (isFuncType(onSuccess)) {
        onSuccess(data);
      }
      return thunkAPI.fulfillWithValue(data);
    } catch (error) {
      if (isFuncType(onError)) {
        onError(error);
      }
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const processMaterialComparison: any = createAsyncThunk(
  PROCESS_DOCUMENT_ACTION,
  async (requestPayload: any, thunkAPI: any) => {
    const { onSuccess, onError, payload } = requestPayload;
    try {
      const { data } = await documentsService.processMaterialComparison(
        payload
      );
      if (isFuncType(onSuccess)) {
        onSuccess(data);
      }
      return thunkAPI.fulfillWithValue(data);
    } catch (error) {
      if (isFuncType(onError)) {
        onError(error);
      }
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const createExtractionReviews: any = createAsyncThunk(
  CREATE_EXTRACTION_REVIEWS_ACTION,
  async (requestPayload: any, thunkAPI: any) => {
    const { onSuccess, onError, fileIds } = requestPayload;
    try {
      const { data } = await documentsService.createExtractionReviews({
        fileIds,
      });
      onSuccess?.(data);
      return thunkAPI.fulfillWithValue(data);
    } catch (error) {
      onError?.(error);
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getExtractionReviews: any = createAsyncThunk(
  GET_EXTRACTION_REVIEWS_ACTION,
  async (requestPayload: any, thunkAPI: any) => {
    const { onSuccess, onError, payload } = requestPayload;
    try {
      const { data } = await documentsService.getExtractionReviews(payload);
      onSuccess?.(data);
      return thunkAPI.fulfillWithValue(data);
    } catch (error) {
      onError?.(error);
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const callExtractionReviewsAI: any = createAsyncThunk(
  CALL_EXTRACTION_REVIEWS_AI_ACTION,
  async (requestPayload: any, thunkAPI: any) => {
    const { onSuccess, onError, id } = requestPayload;
    try {
      const { data } = await documentsService.callExtractionReviewsAI({ id });
      if (isFuncType(onSuccess)) {
        onSuccess(data);
      }
      return thunkAPI.fulfillWithValue(data);
    } catch (error) {
      if (isFuncType(onError)) {
        onError(error);
      }
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getExtractionReviewById: any = createAsyncThunk(
  GET_EXTRACTION_REVIEW_BY_ID_ACTION,
  async (requestPayload: any, thunkAPI: any) => {
    const { onSuccess, onError, id } = requestPayload;
    try {
      const { data } = await documentsService.getExtractionReviewById(id);
      if (isFuncType(onSuccess)) {
        onSuccess(data);
      }
      return thunkAPI.fulfillWithValue(data);
    } catch (error) {
      if (isFuncType(onError)) {
        onError(error);
      }
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const deleteExtractionReview: any = createAsyncThunk(
  DELETE_EXTRACTION_REVIEW_ACTION,
  async (requestPayload: any, thunkAPI: any) => {
    const { onSuccess, onError, id } = requestPayload;
    try {
      const { data } = await documentsService.deleteExtractionReview(id);
      if (isFuncType(onSuccess)) {
        onSuccess(data);
      }
      return thunkAPI.fulfillWithValue(data);
    } catch (error) {
      if (isFuncType(onError)) {
        onError(error);
      }
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const retryDocument: any = createAsyncThunk(
  RETRY_DOCUMENT_ACTION,
  async (requestPayload: any, thunkAPI: any) => {
    const { onSuccess, onError, payload } = requestPayload;
    try {
      const { data } = await documentsService.retryDocument(payload);
      if (isFuncType(onSuccess)) {
        onSuccess(data);
      }
      return thunkAPI.fulfillWithValue(data);
    } catch (error) {
      if (isFuncType(onError)) {
        onError(error);
      }
      return thunkAPI.rejectWithValue(error);
    }
  }
);
